import { classApis } from '@/api/class';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { IClassInitialState, IClassesSummaryGetRequest, IThunkAPI } from '../types';
import { RootState } from '..';

export const initialState: IClassInitialState = {
  classList: undefined,
  classFilters: undefined,
  classAndSessionDetails: undefined,
  classTrainers: undefined,
  selectedClientId: 0,
};

// get class filters
export const getClassFilters = createAsyncThunk<any, void, IThunkAPI>('class/filters', async (_, thunkAPI) => {
  try {
    const response = await classApis.getFilters();

    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

// get class filters
export const getClassFiltersForGig = createAsyncThunk<any, number, IThunkAPI>(
  'class/gig/filters',
  async (gigId, thunkAPI) => {
    try {
      const response = await classApis.getFiltersForGig(gigId);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

// list classes
export const getClassList = createAsyncThunk<any, IClassesSummaryGetRequest, IThunkAPI>(
  'class/list',
  async (payload, thunkAPI) => {
    try {
      const response = await classApis.getAllClasses(payload);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

// get class and session details
export const getClassAndSessionDetails = createAsyncThunk<any, number, IThunkAPI>(
  'class/sessions',
  async (clientId, thunkAPI) => {
    try {
      const response = await classApis.getClassAndSessionDetails(clientId);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

// get attedance details
export const getClassAttendanceDetails = createAsyncThunk<any, number, IThunkAPI>(
  'class/attendance',
  async (clientId, thunkAPI) => {
    try {
      const response = await classApis.getClassAttendanceDetails(clientId);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getClassTrainers = createAsyncThunk<any, number, IThunkAPI>(
  'class/trainers',
  async (clientId, thunkAPI) => {
    try {
      const response = await classApis.getClassTrainers(clientId);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

// set selected client
export const setSelectedClient = createAsyncThunk<any, number, IThunkAPI>('class/setClient', async (clientId, _) => {
  return clientId;
});

// get class user count
export const getClassUserCount = createAsyncThunk<any, number, IThunkAPI>(
  'class/userCount',
  async (clientId, thunkAPI) => {
    try {
      const response = await classApis.getClassUserCount(clientId);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

// get class user count
export const deleteClass = createAsyncThunk<any, number, IThunkAPI>('class/delete', async (clientId, thunkAPI) => {
  try {
    const response = await classApis.deleteClass(clientId);

    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

// get class country applicant count
export const getClassCountryApplicantCount = createAsyncThunk<any, number, IThunkAPI>(
  'class/applicantCount',
  async (clientId, thunkAPI) => {
    try {
      const response = await classApis.getClassCountryApplicantCount(clientId);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const classSlice = createSlice({
  name: 'classSlice',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getClassList.fulfilled, (state, action) => {
      state.classList = action.payload;
    });
    builder.addCase(getClassFilters.fulfilled, (state, action) => {
      state.classFilters = action.payload;
    });
    builder.addCase(getClassFiltersForGig.fulfilled, (state, action) => {
      state.classFilters = action.payload;
    });
    builder.addCase(getClassAndSessionDetails.fulfilled, (state, action) => {
      state.classFilters = action.payload.result;
    });
    builder.addCase(getClassTrainers.fulfilled, (state, action) => {
      state.classTrainers = action.payload.result;
    });
    builder.addCase(setSelectedClient.fulfilled, (state, action) => {
      state.selectedClientId = action.payload;
    });
  },
});

export const classListSelector = (state: RootState): IClassInitialState['classList'] => state.class?.classList;

export default classSlice.reducer;
